/**
 * Environment configuration for the Schedule Widget
 * Provides environment-specific settings based on the environment parameter
 */

export interface EnvironmentConfig {
  baseUrl: string;
  apiTimeout: number;
  enableLogging: boolean;
  retryAttempts: number;
}

export type Environment = 'app' | 'dev' | 'local';

/**
 * Environment configurations for different deployment environments
 */
const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  app: {
    baseUrl: 'https://app.getyomojo.com',
    apiTimeout: 10000, // 10 seconds
    enableLogging: false,
    retryAttempts: 3,
  },
  dev: {
    baseUrl: 'https://dev.getyomojo.com',
    apiTimeout: 15000, // 15 seconds for dev (might be slower)
    enableLogging: true,
    retryAttempts: 2,
  },
  local: {
    baseUrl: 'http://localhost:1337',
    apiTimeout: 30000, // 30 seconds for local development
    enableLogging: true,
    retryAttempts: 1,
  },
};

/**
 * Get environment configuration based on environment parameter
 * @param environment - The environment parameter from URL
 * @returns Environment configuration object
 */
export const getEnvironmentConfig = (environment: string = 'app'): EnvironmentConfig => {
  // Normalize environment string to lowercase
  const normalizedEnv = environment.toLowerCase() as Environment;
  
  // Check if the environment is valid, default to 'app' if not
  if (!environmentConfigs[normalizedEnv]) {
    console.warn(`Unknown environment '${environment}', defaulting to 'app'`);
    return environmentConfigs.app;
  }
  
  return environmentConfigs[normalizedEnv];
};

/**
 * Get the base URL for API calls based on environment
 * @param environment - The environment parameter from URL
 * @returns Base URL string for API calls
 */
export const getBaseUrl = (environment: string = 'app'): string => {
  const config = getEnvironmentConfig(environment);
  return config.baseUrl;
};

/**
 * Check if the current environment is development or local
 * @param environment - The environment parameter from URL
 * @returns True if development environment (dev or local)
 */
export const isDevelopmentEnvironment = (environment: string = 'app'): boolean => {
  const normalizedEnv = environment.toLowerCase();
  return normalizedEnv === 'dev' || normalizedEnv === 'local';
};

/**
 * Log function that only logs in development environments
 * @param environment - The environment parameter from URL
 * @param message - Message to log
 * @param data - Optional data to log
 */
export const envLog = (environment: string, message: string, data?: any): void => {
  const config = getEnvironmentConfig(environment);
  if (config.enableLogging) {
    console.log(`[${environment.toUpperCase()}] ${message}`, data || '');
  }
};

const EnvironmentUtils = {
  getEnvironmentConfig,
  getBaseUrl,
  isDevelopmentEnvironment,
  envLog,
};

export default EnvironmentUtils;
